<template>
    <div class="add-to-catalog-piece-content">
        <Info :elevation="1" class="mb-4">
            {{ $translate("pieceContentDescription", { count: items.length }) }}
        </Info>

        <Block class="add-to-catalog-piece-content__bulk mb-4">
            <template slot="title">{{ $translate("bulkUpdateTitle") }}</template>
            <div class="add-to-catalog-piece-content__bulk-layout">
                <div class="add-to-catalog-piece-content__bulk-fields">
                    <FormatSelect
                        :value="bulkProductFormat"
                        :formats="formats"
                        :label="$translate('labels.bulkProductFormat')"
                        :format-filter="bulkFormatFilter"
                        :show-create-option="canCreateFormat"
                        variant="grouped"
                        clearable
                        input-class="add-to-catalog-piece-content__bulk-format"
                        @input="onBulkProductFormatChange"
                        @create-format="createProductFormat(null)"
                    />
                    <div
                        v-if="bulkIsCustom"
                        class="add-to-catalog-piece-content__bulk-custom-fields"
                    >
                        <v-row dense>
                            <v-col cols="6">
                                <v-text-field
                                    :value="bulkQuantity"
                                    :label="$translate('labels.bulkQuantity')"
                                    dense
                                    outlined
                                    hide-details
                                    type="text"
                                    inputmode="decimal"
                                    clearable
                                    @input="
                                        bulkQuantity =
                                            $utils.normalizeDecimalString($event)
                                    "
                                />
                            </v-col>
                            <v-col cols="6">
                                <v-select
                                    v-model="bulkUnit"
                                    :items="bulkPieceContentUnitOptions"
                                    :label="$translate('labels.bulkUnit')"
                                    dense
                                    outlined
                                    hide-details
                                    clearable
                                />
                            </v-col>
                        </v-row>
                    </div>
                </div>
                <div class="add-to-catalog-piece-content__bulk-actions">
                    <template v-if="bulkIsCustom">
                        <v-btn
                            small
                            color="primary"
                            @click="applyBulkProductFormat(false)"
                        >
                            {{ $translate("buttons.applyCustomAll") }}
                        </v-btn>
                        <v-btn
                            small
                            text
                            color="primary"
                            @click="applyBulkProductFormat(true)"
                        >
                            {{ $translate("buttons.applyFormatEmpty") }}
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn
                            small
                            color="primary"
                            :disabled="!bulkProductFormat"
                            @click="applyBulkProductFormat(false)"
                        >
                            {{ $translate("buttons.applyFormatAll") }}
                        </v-btn>
                        <v-btn
                            small
                            text
                            color="primary"
                            :disabled="!bulkProductFormat"
                            @click="applyBulkProductFormat(true)"
                        >
                            {{ $translate("buttons.applyFormatEmpty") }}
                        </v-btn>
                    </template>
                </div>
            </div>
            <p class="text-caption grey--text text--darken-1 mb-0 mt-3">
                {{ bulkHelpText }}
            </p>
        </Block>

        <v-card
            v-for="item in items"
            :key="item.id"
            outlined
            class="add-to-catalog-piece-content__card mb-3"
        >
            <v-card-text class="py-4">
                <div class="add-to-catalog-piece-content__card-layout">
                    <div class="add-to-catalog-piece-content__card-product">
                        <ProductName :product="item" />
                    </div>
                    <div class="add-to-catalog-piece-content__card-form">
                        <FormatSelect
                            :value="formFor(item.id).product_format_id"
                            :formats="formats"
                            :label="$translate('labels.productFormat')"
                            :error-messages="fieldErrors(item.id, 'product_format')"
                            :format-filter="(format) => productFormatFilterFor(item, format)"
                            :show-create-option="canCreateFormat"
                            variant="grouped"
                            @input="onProductFormatChange(item.id, $event)"
                            @create-format="createProductFormat(item.id)"
                        />
                        <div
                            v-if="isCustomForm(item.id)"
                            class="add-to-catalog-piece-content__custom-fields"
                        >
                            <v-row dense>
                                <v-col cols="6">
                                    <v-text-field
                                        :value="formFor(item.id).quantity"
                                        :label="
                                            $translate(
                                                'labels.pieceContentQuantity',
                                            )
                                        "
                                        :error-messages="
                                            fieldErrors(item.id, 'quantity')
                                        "
                                        dense
                                        outlined
                                        hide-details="auto"
                                        type="text"
                                        inputmode="decimal"
                                        @input="
                                            updateForm(item.id, 'quantity', $event)
                                        "
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <v-select
                                        :value="formFor(item.id).unit"
                                        :items="pieceContentUnitOptionsFor(item)"
                                        :label="
                                            $translate('labels.pieceContentUnit')
                                        "
                                        :error-messages="
                                            fieldErrors(item.id, 'unit')
                                        "
                                        dense
                                        outlined
                                        hide-details="auto"
                                        @change="
                                            updateForm(item.id, 'unit', $event)
                                        "
                                    />
                                </v-col>
                            </v-row>
                        </div>
                        <v-chip
                            v-if="stockEquivalence(item)"
                            small
                            label
                            color="info"
                            outlined
                            class="add-to-catalog-piece-content__equivalence-chip mt-3 mb-0"
                        >
                            <v-icon left x-small>mdi-swap-horizontal</v-icon>
                            {{ stockEquivalence(item) }}
                        </v-chip>
                    </div>
                </div>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import GlobalMixins from "@/mixins/global";
import FormatModal from "@/modals/format.vue";
import { FORMAT_SELECT_CUSTOM } from "@/constants/format-select";

export default {
    name: "AddToCatalogPieceContent",
    mixins: [GlobalMixins],
    data() {
        return {
            bulkProductFormat: null,
            bulkQuantity: "",
            bulkUnit: null,
        };
    },
    computed: {
        ...mapState("addtocatalog", [
            "selected_items",
            "formats",
            "piece_content_forms",
        ]),
        pieceContentUnitOptions() {
            return this.$utils.getPieceContentUnitOptions();
        },
        canCreateFormat() {
            return this.userHasPermission("catalog.add_format");
        },
        bulkIsCustom() {
            return this.bulkProductFormat === FORMAT_SELECT_CUSTOM;
        },
        bulkPieceContentUnitOptions() {
            const dimensions = new Set(
                this.items
                    .map((item) =>
                        this.$utils.getAddToCatalogSaleFormatDimension(
                            item.add_format,
                            this.formats,
                        ),
                    )
                    .filter(Boolean),
            );
            if (dimensions.size !== 1) {
                return this.pieceContentUnitOptions;
            }
            const [saleDimension] = dimensions;
            return this.pieceContentUnitOptions.filter(
                (option) =>
                    this.$utils.getUnitDimension(option.value) === saleDimension,
            );
        },
        bulkHelpText() {
            if (this.bulkIsCustom) {
                return this.$translate("hints.bulkCustomHelp");
            }
            return this.$translate("hints.bulkFormatHelp");
        },
        items() {
            return this.selected_items.filter((item) => {
                if (this.$utils.getPieceContent(item)) {
                    return false;
                }
                return this.$utils.productNeedsPieceContentForAddToCatalog(
                    item,
                    item.add_format,
                    this.formats,
                );
            });
        },
    },
    methods: {
        ...mapMutations("addtocatalog", [
            "updatePieceContentForm",
            "updatePieceContentProductFormat",
            "applyBulkPieceContentProductFormat",
            "applyBulkPieceContentQuantity",
            "applyBulkPieceContentUnit",
            "appendFormat",
        ]),
        formFor(productId) {
            return (
                this.piece_content_forms[productId] || {
                    product_format_id: null,
                    custom: false,
                    quantity: "",
                    unit: null,
                    errors: {},
                }
            );
        },
        isCustomForm(productId) {
            const form = this.formFor(productId);
            return (
                form.custom
                || form.product_format_id === FORMAT_SELECT_CUSTOM
            );
        },
        fieldErrors(productId, field) {
            const keys = this.formFor(productId).errors?.[field] || [];
            return keys.map((key) => this.$translate(`errors.${key}`));
        },
        bulkFormatFilter(format) {
            return this.items.every((entry) =>
                this.$utils.unitFormatCompatibleWithAddToCatalogSaleFormat(
                    entry,
                    entry.add_format,
                    this.formats,
                    format,
                ),
            );
        },
        productFormatFilterFor(item, format) {
            return this.$utils.unitFormatCompatibleWithAddToCatalogSaleFormat(
                item,
                item.add_format,
                this.formats,
                format,
            );
        },
        pieceContentUnitOptionsFor(item) {
            return this.$utils.getPieceContentUnitOptionsForAddToCatalogSaleFormat(
                item.add_format,
                this.formats,
            );
        },
        onProductFormatChange(productId, formatId) {
            this.updatePieceContentProductFormat({
                productId,
                formatId,
            });
        },
        onBulkProductFormatChange(formatId) {
            this.bulkProductFormat = formatId;
        },
        async createProductFormat(productId) {
            try {
                const result = await this.showModal({
                    component: FormatModal,
                    params: {},
                });
                if (!result?.id) {
                    return;
                }
                this.appendFormat(result);
                if (productId) {
                    this.updatePieceContentProductFormat({
                        productId,
                        formatId: result.id,
                    });
                    return;
                }
                this.bulkProductFormat = result.id;
                this.applyBulkProductFormat(false);
            } catch (error) {
                if (error) {
                    this.$swal({
                        title: this.$translate("errors.createFormatFailed"),
                        html: this.$utils.resultToSwalContent(this, error),
                        type: "error",
                    });
                }
            }
        },
        updateForm(productId, field, value) {
            const normalized =
                field === "quantity"
                    ? this.$utils.normalizeDecimalString(value)
                    : value;
            this.updatePieceContentForm({
                productId,
                field,
                value: normalized,
            });
        },
        applyBulkProductFormat(onlyEmpty) {
            if (!this.bulkProductFormat) {
                return;
            }
            this.applyBulkPieceContentProductFormat({
                formatId: this.bulkProductFormat,
                onlyEmpty,
            });
            if (this.bulkProductFormat !== FORMAT_SELECT_CUSTOM) {
                return;
            }
            if (this.bulkQuantity) {
                this.applyBulkPieceContentQuantity({
                    quantity: this.bulkQuantity,
                    onlyEmpty,
                });
            }
            if (this.bulkUnit) {
                this.applyBulkPieceContentUnit({
                    unit: this.bulkUnit,
                    onlyEmpty,
                });
            }
        },
        stockEquivalence(item) {
            const form = this.formFor(item.id);
            const preview = this.$utils.buildPreviewCatalogEntryWithPieceContent(
                item,
                item.add_format,
                this.formats,
                form,
            );
            if (
                !this.$utils.catalogEntryCanConvertSaleToStock(preview, 1)
            ) {
                return null;
            }
            const stockQty = this.$utils.convertEntrySaleToStockQuantity(
                preview,
                1,
            );
            if (stockQty == null) {
                return null;
            }
            const stockCfg = this.$utils.getProductQuantityInputConfig(
                item.stock_unit || "pc",
                true,
            );
            const formatted = this.$utils.formatQuantityWithShortUnit(
                stockQty,
                stockCfg.unitType,
            );
            return this.$translate("hints.stockEquivalence", {
                quantity: formatted,
            });
        },
    },
    i18n: {
        messages: {
            en: {
                pieceContentDescription:
                    "%{count} product(s) need the unit format before they can be added with a volume/weight sale format.",
                bulkUpdateTitle: "Bulk update",
                labels: {
                    bulkProductFormat: "Unit format",
                    bulkQuantity: "Quantity",
                    bulkUnit: "Unit",
                    productFormat: "Unit format",
                    customFormat: "Customize",
                    createFormat: "Create a format",
                    pieceContentQuantity: "Content per piece",
                    pieceContentUnit: "Unit",
                },
                hints: {
                    bulkFormatHelp:
                        "Apply the same unit format to every row, or only to rows without a selection.",
                    bulkCustomHelp:
                        "Apply a custom content per piece to all rows or only empty fields.",
                    stockEquivalence: "1 sale unit = %{quantity} in stock",
                },
                buttons: {
                    applyFormatAll: "Apply format to all",
                    applyFormatEmpty: "Fill empty only",
                    applyCustomAll: "Apply customize to all",
                },
                errors: {
                    pieceContentFormatRequired: "Unit format is required.",
                    pieceContentQuantityRequired: "Quantity is required.",
                    pieceContentUnitRequired: "Unit is required.",
                    pieceContentIncompatibleWithSaleFormat:
                        "Incompatible with the selected sale format.",
                    createFormatFailed: "Could not create format",
                },
            },
            fr: {
                pieceContentDescription:
                    "%{count} produit(s) nécessitent le format à l'unité avant d'être ajoutés avec un format de vente en volume/poids.",
                bulkUpdateTitle: "Mise à jour groupée",
                labels: {
                    bulkProductFormat: "Format à l'unité",
                    bulkQuantity: "Quantité",
                    bulkUnit: "Unité",
                    productFormat: "Format à l'unité",
                    customFormat: "Personaliser",
                    createFormat: "Créer un format",
                    pieceContentQuantity: "Contenu par pièce",
                    pieceContentUnit: "Unité",
                },
                hints: {
                    bulkFormatHelp:
                        "Appliquez le même format à l'unité à toutes les lignes, ou seulement aux lignes sans sélection.",
                    bulkCustomHelp:
                        "Appliquez un contenu par pièce personnalisé à toutes les lignes ou seulement aux champs vides.",
                    stockEquivalence: "1 unité de vente = %{quantity} en stock",
                },
                buttons: {
                    applyFormatAll: "Appliquer le format à tous",
                    applyFormatEmpty: "Remplir les vides seulement",
                    applyCustomAll: "Appliquer Personaliser à tous",
                },
                errors: {
                    pieceContentFormatRequired:
                        "Le format à l'unité est obligatoire.",
                    pieceContentQuantityRequired: "La quantité est obligatoire.",
                    pieceContentUnitRequired: "L'unité est obligatoire.",
                    pieceContentIncompatibleWithSaleFormat:
                        "Incompatible avec le format de vente sélectionné.",
                    createFormatFailed: "Impossible de créer le format",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.add-to-catalog-piece-content__bulk-layout {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 24px;
    align-items: flex-start;
}

.add-to-catalog-piece-content__bulk-fields {
    flex: 0 1 320px;
    width: 100%;
    max-width: 360px;
}

.add-to-catalog-piece-content__bulk-format {
    width: 100%;
}

.add-to-catalog-piece-content__bulk-custom-fields {
    margin-top: 8px;
}

.add-to-catalog-piece-content__bulk-actions {
    flex: 1 1 240px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: flex-end;
    gap: 8px;
    margin-left: auto;
}

.add-to-catalog-piece-content__card:last-child {
    margin-bottom: 0 !important;
}

.add-to-catalog-piece-content__card-layout {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 24px;
    align-items: flex-start;
}

.add-to-catalog-piece-content__card-product {
    flex: 1 1 240px;
    min-width: 0;
}

.add-to-catalog-piece-content__card-form {
    flex: 0 1 320px;
    width: 100%;
    max-width: 360px;
    margin-left: auto;
}

.add-to-catalog-piece-content__custom-fields {
    margin-top: 8px;
}

.add-to-catalog-piece-content__equivalence-chip {
    max-width: 100%;
    height: auto;
    min-height: 24px;
    white-space: normal;
}

@media (max-width: 959px) {
    .add-to-catalog-piece-content__bulk-fields {
        max-width: none;
    }

    .add-to-catalog-piece-content__bulk-actions {
        margin-left: 0;
        justify-content: flex-start;
    }

    .add-to-catalog-piece-content__card-form {
        max-width: none;
        margin-left: 0;
    }
}
</style>
